import { Badge, Grid, styled } from "@mui/material";
import { IShareWaItem, useSharedWa } from "@rf-hooks/useSharedWa";
import useAlert from "@rf-hooks/useAlert";
import MxButton2 from "@rf-l1/buttons/MxButton2";
import React, {
  createContext,
  useRef,
  useCallback,
  useMemo,
  ReactNode,
  useEffect,
  useState
} from "react";
import usePropertyShareReferPropertyModal from "./useReferProperty";
import { IconBrandWhatsapp, IconTrash } from "@tabler/icons-react";

interface SharePropertyContextType {
  isChecked: (itemId: any) => boolean;
  getItemsSelected: () => any[];
  addItem: (item: any) => void;
  removeItem: (itemId: any) => void;
  sendWs: () => void;
  sendReferProperty: () => void;
  resetProperty: () => void;
}

export const SharePropertyContext = createContext<SharePropertyContextType | null>(
  null
);

interface SharePropertyProviderProps {
  children: ReactNode;
}

const SharePropertyProvider: React.FC<SharePropertyProviderProps> = ({
  children
}) => {
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const { changeBadge, refBadgeWs, refBadgeRefer, refContentButtons } =
    useRefItems();
  const { sharedWa } = useSharedWa();
  const { handleOpenReferProperty } = usePropertyShareReferPropertyModal();
  const { warningAlert } = useAlert();
  const isChecked = useCallback(
    (itemId: any) => selectedItems.some(item => item.id === itemId),
    [selectedItems]
  );

  const getItemsSelected = useCallback(() => {
    return [...selectedItems];
  }, [selectedItems]);

  const addItem = useCallback(
    (item: any) => {
      setSelectedItems(prevItems => {
        if (prevItems.some(currentItem => currentItem.id === item.id)) {
          return prevItems;
        }
        return [...prevItems, item];
      });
    },
    []
  );

  const removeItem = useCallback((itemId: any) => {
    setSelectedItems(prevItems =>
      prevItems.filter(item => item.id !== itemId)
    );
  }, []);

  const sendWs = useCallback(() => {
    const items: IShareWaItem[] = selectedItems.map(item => {
      return {
        id: item.id,
        typeProperty: item.typeProperty,
        businessType: item.businessType,
        sector: item.sector,
        title: item.title
      };
    });
    sharedWa(items);
  }, [selectedItems, sharedWa]);

  const sendReferProperty = useCallback(() => {
    handleOpenReferProperty(selectedItems);
  }, [handleOpenReferProperty, selectedItems]);

  const resetProperty = useCallback(() => {
    if (!selectedItems.length) return;
    warningAlert({
      title: "Deshacer selección",
      message: "¿Deseas quitar la selección de todas las propiedades?",
      confirmBtnMessage: "Sí, deshacer",
      declineBtnMessage: "Cancelar"
    }).then(response => {
      if (response.type.confirm) {
        setSelectedItems([]);
      }
    });
  }, [selectedItems, warningAlert]);

  useEffect(() => {
    changeBadge(selectedItems.length);
  }, [changeBadge, selectedItems]);

  const contextValue = useMemo<SharePropertyContextType>(
    () => ({
      isChecked,
      getItemsSelected,
      addItem,
      removeItem,
      sendWs,
      sendReferProperty,
      resetProperty
    }),
    [
      isChecked,
      getItemsSelected,
      addItem,
      removeItem,
      sendWs,
      sendReferProperty,
      resetProperty
    ]
  );

  return (
    <SharePropertyContext.Provider value={contextValue}>
      {children}

      <ContentButtons container position="fixed" spacing={2} ref={refContentButtons}>
        <Grid item xs="auto">
          <MxButton2
            variant="contained"
            onClick={resetProperty}
            bgColor="error.main"
            startIcon={<IconTrash size={16} color="white" />}
            testId="reset-all-button"
          >
            Deshacer selección
          </MxButton2>
        </Grid>
        <Grid item xs="auto">
          <Badge badgeContent={1} color="error" ref={refBadgeWs}>
            <MxButton2
              variant="contained"
              onClick={sendWs}
              bgColor="success.main"
              startIcon={<IconBrandWhatsapp size={16} color="white" />}
              testId="send-whatsapp-button"
            >
              Enviar por WhatsApp
            </MxButton2>
          </Badge>
        </Grid>
        <Grid item xs="auto">
          <Badge badgeContent={1} color="error" ref={refBadgeRefer}>
            <MxButton2
              variant="contained"
              onClick={sendReferProperty}
              bgColor="primary.main"
              testId="refer-property-button"
            >
              Referir propiedad
            </MxButton2>
          </Badge>
        </Grid>
      </ContentButtons>
    </SharePropertyContext.Provider>
  );
};

export default SharePropertyProvider; // Keep or remove based on original export style

const useRefItems = () => {
  const refBadgeWs = useRef<HTMLDivElement>(null);
  const refBadgeRefer = useRef<HTMLDivElement>(null);
  const refContentButtons = useRef<HTMLDivElement>(null);
  const changeBadge = (count: number) => {
    const badgeWsElement = refBadgeWs.current?.querySelector(".MuiBadge-badge");
    const badgeReferElement =
      refBadgeRefer.current?.querySelector(".MuiBadge-badge");

    if (badgeWsElement) {
      badgeWsElement.textContent = count.toString();
    }
    if (badgeReferElement) {
      badgeReferElement.textContent = count.toString();
    }
    if (refContentButtons.current) {
      const currentClass = refContentButtons.current
        .getAttribute("class")
        ?.replaceAll(" hidden", "")
        .replaceAll(" show", "");
      refContentButtons.current.setAttribute(
        "class",
        count > 0 ? `${currentClass} show` : `${currentClass} hidden`
      );
    }
  };

  useEffect(() => {
    if (refContentButtons.current) {
      const currentClass = refContentButtons.current.getAttribute("class");
      refContentButtons.current.setAttribute("class", `${currentClass} hidden`);
    }
  }, []);

  return {
    refBadgeWs,
    refBadgeRefer,
    changeBadge,
    refContentButtons
  };
};

const ContentButtons = styled(Grid)(({ theme }) => ({
  width: "auto",
  bottom: 20,
  right: 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  zIndex: 1005,
  transition: "visibility 0s, opacity 0.5s linear",
  "&.show": {
    visibility: "visible",
    opacity: 1
  },
  "&.hidden": {
    visibility: "hidden",
    opacity: 0
  }
}));
